import ArrowRightIcon from '@/icons/ArrowRight';
import SearchIcon from '@/icons/Search';
import { Box, Card, CardHeader, Chip, CircularProgress, debounce, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, 
  MenuItem, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { ListContextProvider, ReduxState, useListContext, useListController, useListFilterContext } from "ra-core";
import { FC, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Content } from "@/components/content/Content";
import DefaultPagination from '@/components/list/pagination/Pagination'
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import { FunctionField, TextField as TdTextField } from "@/components/field";
import useLocalListController from "@/components/list/useLocalListController";
import { RecPatch } from "@/dataProvider/tools";
import { McpServerClientDto, McpServerSearchInput, SecureAIClientDto } from "@/gen/api/secureai";
import { useSelector } from 'react-redux';
import { useResourceRegister } from '@/utils/resourceRegister';



const listOptions : {sorters: Record<string, (a:SecureAIClientDto & RecPatch, b:SecureAIClientDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => (a.clientName || '').toLowerCase() < (b.clientName || '').toLowerCase() ? -1 : 1,
    name: (a, b) => (a.clientName || '').toLowerCase() < (b.clientName || '').toLowerCase() ? -1 : 1,
  }
}

const SecureAIDashboard: FC = () => {
  useResourceRegister({ name: 'secure-ai/mcp-servers/search' })
  const appListCtrl = useLocalListController({ resource: 'secure-ai/app', basePath:''}, listOptions)
  const mcpListCtrl = useListController({ resource: 'secure-ai/mcp-servers/search', basePath:''})
  return (
    <Content pageName="SecureAI Dashboard" resourceName="SecureAI Dashboard">
      <Grid container spacing={4} sx={{ pt: 2 }}>
        <Grid item xs={12} >
          <ListContextProvider value={appListCtrl}>
            <SecureAIAppCard />
          </ListContextProvider>
        </Grid>
        <Grid item xs={12} >
          <ListContextProvider value={mcpListCtrl}>
            <MCPServersCard />
          </ListContextProvider>
        </Grid>
      </Grid>
    </Content>
  )
}

export default SecureAIDashboard;


const SecureAIAppCard : FC = () => {
  const listCtx = useListContext()
  return (
    <Card>
      <CardHeader title="SecureAI App" />
      <Divider />
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150 }}>
          <Datagrid
            hasBulkActions={false}
            sx={{
              '& th': { py: 2 },
              '& td': { py: 2 },
            }}
          >
            <FunctionField label="Instance Name" source="name"
              render={(d?:SecureAIClientDto&RecPatch) => 
                d && (
                  <Typography variant="body2">
                    {d.clientName}
                  </Typography>
                )
              }
            />
            <FunctionField label="Status" source="onlineStatus" sortable={false} render={OnlineStatusRender} />
            <FunctionField label="Action" sortable={false} align="center"
              render={(d?: SecureAIClientDto&RecPatch) => 
                d && 
                <Tooltip title="Detail">
                  <IconButton 
                    sx={{ 
                      color: 'text.primary' as any, 
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    component={RouterLink}
                    to={`/secure-ai/app/${d.clientId ?? ''}/show`}
                  >
                    <ArrowRightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>}
            />
          </Datagrid>
        </Box>
      </Scrollbar>
      {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
    </Card>
  )
}


const MCPServersCard : FC = () => {
  const { setFilters, displayedFilters } = useListFilterContext()
  const [states, setStates] = useState({
    name: '',
    status: '' as string|null,
  })
  const {searchName, searchStatus} = useMemo(() => {
    const invokeSearch = (s1: typeof states) => {
      const input : McpServerSearchInput = {...s1}
      setFilters(input, displayedFilters)
    }
    const debouncedInvokeSearch = debounce(invokeSearch, 300)
    const searchName = (name: string) => {
      setStates(v => {
        const v1 = {...v, name}
        setTimeout(() => debouncedInvokeSearch(v1))
        return v1
      })
    }
    const searchStatus = (status: string|null) => {
      setStates(v => {
        const v1 = {...v, status}
        setTimeout(() => invokeSearch(v1))
        return v1
      })
    }
    return {searchName, searchStatus}
  }, [displayedFilters, setFilters])

  const loading = useSelector<ReduxState, boolean>(store => !!store.admin.loading)

  return (
    <Card>
      <CardHeader title="MCP Servers" 
        action={
          <Stack direction="row" spacing={2}>
            <TextField 
              id="mcp-search-name"
              placeholder='Search by Name/Database'
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {loading ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
                  </InputAdornment>
                )
              }}
              onChange={(e) => searchName(e.target.value)}
              value={states.name}
              variant="outlined"
              sx={{ width: 280 }}
            />
            <Box sx={{ width: 120 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="mcp-status-filter-label">Status</InputLabel>
                <Select label="Status" fullWidth variant="outlined" id="mcp-status-filter-select"
                  labelId="mcp-status-filter-label"
                  onChange={(e) => searchStatus(e.target.value)}
                  value={states.status}
                >
                  <MenuItem key="_empty_" value=""><em>All</em></MenuItem>
                  <MenuItem key="Online" value="Online">Online</MenuItem>
                  <MenuItem key="Offline" value="Offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        }
      />
      <Divider />
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150 }}>
          <Datagrid
            hasBulkActions={false}
            sx={{
              '& th': { py: 2 },
              '& td': { py: 2 },
            }}
          >
            <FunctionField label="Instance Name" source="name" 
              render={(d?:McpServerClientDto&RecPatch) => 
                d && (
                  d.description
                  ? <Tooltip title={d.description} >
                      <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                        {d.name}
                      </Typography>
                    </Tooltip>
                  : <Typography variant="body2">
                      {d.name}
                    </Typography>
                )
              }
            />
            <TdTextField label="DB Name" source="databaseName" sortable={false} />
            <FunctionField label="DB Engine/Edition" source="databaseEngine" sortable={false}
              render={(d?:McpServerClientDto&RecPatch) => 
                d &&
                <Typography variant="body2">
                  {d.databaseEngine}
                </Typography>
              }
            />
            <FunctionField label="Status" source="onlineStatus" sortable={false} render={OnlineStatusRender} />
            <FunctionField label="Action" sortable={false} align="center"
              render={(d?: McpServerClientDto&RecPatch) => 
                d && 
                <Tooltip title="Detail">
                  <IconButton 
                    sx={{ 
                      color: 'text.primary' as any, 
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    component={RouterLink}
                    to={`/secure-ai/mcp-servers/${(d as any).clientId ?? ''}/show`}
                  >
                    <ArrowRightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>}
            />
          </Datagrid>
        </Box>
      </Scrollbar>
      {//@ts-ignore 
        <DefaultPagination />}
    </Card>
  )
}


const OnlineStatusRender = (rec?: any) => {
  const x = rec as { onlineStatus: string|null|undefined, id:any }|undefined
  if (!x || !x.onlineStatus) { return null }
  const color = x.onlineStatus.toLowerCase() === 'online' ? 'success' : 'warning'
  return (
    <Chip label={x.onlineStatus} color={color} size="small" />
  )
}